import { useState } from "react";

const FormModal = ({ isOpen, onClose, activeTab }) => {
  const [thankYouDialogOpen, setThankYouDialogOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedRole, setSelectedRole] = useState(activeTab); // Default to activeTab

  // Google Form action URL
  const googleFormAction =
    "https://docs.google.com/forms/u/0/d/e/1FAIpQLSdx-qrrSiMnzAhTbAaxU7UJfj0aZJlyPgCl3smjVFaWh4k-xA/formResponse";

  // Field IDs from the Google Form (aligned with HeroSection)
  const fieldNames = {
    name: "entry.271377800",
    mobile: "entry.1059613645",
    email: "entry.1361867851",
    studentTeacher: "entry.2132025188",
    locality: "entry.505168970",
    classGrade: "entry.2115027460",
    qualification: "entry.1272021577",
  };

  // Form validation function
  const validateForm = (formData) => {
    const newErrors = {};
    const name = formData.get(fieldNames.name);
    if (!name || !/^[a-zA-Z\s]+$/.test(name)) {
      newErrors[fieldNames.name] =
        "Enter a valid name (letters and spaces only)";
    }
    const mobile = formData.get(fieldNames.mobile);
    if (!mobile || !/^[6-9]\d{9}$/.test(mobile)) {
      newErrors[fieldNames.mobile] =
        "Enter a valid 10-digit Indian number (6-9 start)";
    }

    if (selectedRole === "student") {
      const locality = formData.get(fieldNames.locality);
      if (!locality) {
        newErrors[fieldNames.locality] = "Address is required";
      }
      const classGrade = formData.get(fieldNames.classGrade);
      if (!classGrade) {
        newErrors[fieldNames.classGrade] = "Class/Grade is required";
      }
    } else {
      // Tutor
      const email = formData.get(fieldNames.email);
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        newErrors[fieldNames.email] = "Enter a valid email address";
      }
      const qualification = formData.get(fieldNames.qualification);
      if (!qualification) {
        newErrors[fieldNames.qualification] = "Please select a qualification";
      }
    }
    return newErrors;
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);
    const validationErrors = validateForm(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    try {
      await fetch(googleFormAction, {
        method: "POST",
        body: formData,
        mode: "no-cors",
      });
      setThankYouDialogOpen(true);
      form.reset();
      setErrors({});
      onClose(); // Close the form modal on success
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("There was an error submitting the form. Please try again."); // Fallback error handling
    }
  };

  // Thank You Dialog component
  const ThankYouDialog = ({ isOpen, onClose }) => {
    if (!isOpen) return null;
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50">
        <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl animate-fadeIn">
          {/* <h2 className="text-2xl font-bold text-blue-600 mb-4">
            Woohoo, You’re a Star!
          </h2> */}
          <p className="text-gray-700 mb-6">
            Thank you. Your details have been successfully submitted, and Tutor
            Kendra team will contact you shortly.
          </p>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-blue-500 text-white rounded-3xl hover:bg-blue-600 transition-all"
          >
            Close
          </button>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 rounded-lg shadow-lg w-full max-w-md mx-4">
          <h2 className="text-xl font-bold mb-4">
            {selectedRole === "student" ? "Book a Tutor" : "Register as Tutor"}
          </h2>
          <div className="flex gap-4 mb-4">
            <button
              type="button"
              onClick={() => setSelectedRole("student")}
              className={`px-4 py-2 rounded-3xl ${
                selectedRole === "student"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-300 text-black"
              } hover:bg-blue-600 hover:text-white transition-colors`}
            >
              Student
            </button>
            <button
              type="button"
              onClick={() => setSelectedRole("tutor")}
              className={`px-4 py-2 rounded-3xl ${
                selectedRole === "tutor"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-300 text-black"
              } hover:bg-blue-600 hover:text-white transition-colors`}
            >
              Tutor
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="hidden"
              name={fieldNames.studentTeacher}
              value={selectedRole === "student" ? "Student" : "Tutor"}
            />
            <div>
              <input
                type="text"
                name={fieldNames.name}
                placeholder="Enter your name"
                className="w-full px-4 py-3 rounded-3xl bg-white text-black border drop-shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-700"
                required
              />
              {errors[fieldNames.name] && (
                <p className="text-red-500 text-sm mt-1">
                  {errors[fieldNames.name]}
                </p>
              )}
            </div>
            <div>
              <input
                type="text"
                name={fieldNames.mobile}
                placeholder="+91 Enter mobile number"
                className="w-full px-4 py-3 rounded-3xl bg-white text-black border drop-shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-700"
                required
              />
              {errors[fieldNames.mobile] && (
                <p className="text-red-500 text-sm mt-1">
                  {errors[fieldNames.mobile]}
                </p>
              )}
            </div>
            {selectedRole === "student" ? (
              <>
                <div>
                  <input
                    type="text"
                    name={fieldNames.locality}
                    placeholder="Enter your address"
                    className="w-full px-4 py-3 rounded-3xl bg-white text-black border drop-shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-700"
                    required
                  />
                  {errors[fieldNames.locality] && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors[fieldNames.locality]}
                    </p>
                  )}
                </div>
                <div>
                  <input
                    type="text"
                    name={fieldNames.classGrade}
                    placeholder="Enter Class/Grade"
                    className="w-full px-4 py-3 rounded-3xl bg-white text-black border drop-shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-700"
                    required
                  />
                  {errors[fieldNames.classGrade] && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors[fieldNames.classGrade]}
                    </p>
                  )}
                </div>
              </>
            ) : (
              <>
                <div>
                  <input
                    type="email"
                    name={fieldNames.email}
                    placeholder="Enter your email"
                    className="w-full px-4 py-3 rounded-3xl bg-white text-black border drop-shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-700"
                    required
                  />
                  {errors[fieldNames.email] && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors[fieldNames.email]}
                    </p>
                  )}
                </div>
                <div>
                  <select
                    name={fieldNames.qualification}
                    className="w-full px-4 py-3 rounded-3xl bg-white text-black border drop-shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="" disabled selected>
                      Select Qualification
                    </option>
                    <option value="12th">12th</option>
                    <option value="graduation">Graduation</option>
                    <option value="post-graduation">Post-Graduation</option>
                  </select>
                  {errors[fieldNames.qualification] && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors[fieldNames.qualification]}
                    </p>
                  )}
                </div>
              </>
            )}
            <div className="flex justify-between">
              <button
                type="submit"
                className="px-6 py-3 bg-blue-500 text-white rounded-3xl hover:bg-blue-600"
              >
                {selectedRole === "student" ? "Book Now" : "Register as Tutor"}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 bg-gray-500 text-white rounded-3xl hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
      <ThankYouDialog
        isOpen={thankYouDialogOpen}
        onClose={() => setThankYouDialogOpen(false)}
      />
    </>
  );
};

export default FormModal;
